import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { Sparkles, FileText, Loader2, User, RotateCcw } from "lucide-react";
import type { LinkedInJob, TechManager, CvDocumentSlim } from "../api/LinkedInJobsApi";
import {
  fetchTechManagers,
  fetchEmployeeCvsForAdmin,
  enhanceCvForJob,
} from "../api/LinkedInJobsApi";
import { listDocuments } from "features/cv-processor/api/CvProcessorApi";
import type { CvDocument } from "features/cv-processor/api/CvProcessorApi";
import EnhancedCvModal from "features/cv-processor/ui/EnhancedCvModal";
import EnhancementLogsModal from "./EnhancementLogsModal";

type Phase = "idle" | "enhancing" | "done" | "error";

export interface CvEnhancementSectionHandle {
  runEnhancement: () => Promise<void>;
  canEnhance: boolean;
  selectedCvId: number | "";
}

interface Props {
  job: LinkedInJob;
  isAdmin: boolean;
  techManager?: TechManager | null;
  initialEnhancedData?: any;
  jobSummary?: string | null;
  /** When false, hides this section's own idle-state "Enhance" button — used when a
   * parent wraps this alongside CoverLetterEnhancementSection under one combined
   * "Enhance All" trigger. The CV/TM pickers and the done-state "Re-Enhance" button
   * are unaffected — only the first-run trigger is suppressed. Defaults to true so
   * standalone usage is unchanged. */
  showOwnTrigger?: boolean;
  /** Notifies the parent whenever the selected CV changes, so a sibling section
   * (e.g. cover letter) can auto-match on the CV's category_id. */
  onCvSelected?: (cvId: number | "", categoryId: number | null) => void;
}

const CvEnhancementSection = forwardRef<CvEnhancementSectionHandle, Props>(function CvEnhancementSection(
  { job, isAdmin, techManager, initialEnhancedData, jobSummary, showOwnTrigger = true, onCvSelected },
  ref,
) {
  const [phase, setPhase] = useState<Phase>(initialEnhancedData ? "done" : "idle");
  const [techManagers, setTechManagers] = useState<TechManager[]>([]);
  const [cvList, setCvList] = useState<CvDocumentSlim[] | null>(null);
  const [cvLoading, setCvLoading] = useState(false);
  const [selectedEmployeeCode, setSelectedEmployeeCode] = useState<string>("");
  const [selectedCvId, setSelectedCvId] = useState<number | "">("");
  const [enhancedData, setEnhancedData] = useState<CvDocument | null>(initialEnhancedData ?? null);
  const [showModal, setShowModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [hasLogs, setHasLogs] = useState(job.has_enhancement_logs ?? false);
  // alertId tracks the resolved alert_id — may differ from job.alert_id on first-time enhancement
  // where the alert is created server-side and the job prop hasn't been refreshed yet.
  const [alertId, setAlertId] = useState<number | undefined>(job.alert_id);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const phaseRef = useRef<Phase>(phase);

  useEffect(() => { return () => { mountedRef.current = false; }; }, []);
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  // Sync hasLogs and alertId when the parent refreshes the job prop.
  useEffect(() => {
    if (job.has_enhancement_logs) setHasLogs(true);
    if (job.alert_id) setAlertId(job.alert_id);
  }, [job.has_enhancement_logs, job.alert_id]);

  useEffect(() => {
    if (initialEnhancedData && (phaseRef.current === "idle" || phaseRef.current === "error")) {
      setEnhancedData(initialEnhancedData as CvDocument);
      setPhase("done");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEnhancedData]);

  // Pre-select TM if job already has one linked
  useEffect(() => {
    if (isAdmin && techManager) setSelectedEmployeeCode(techManager.employee_code);
  }, [isAdmin, techManager]);

  // Load TM list for admin without a pre-linked TM
  useEffect(() => {
    if (!isAdmin || techManager) return;
    fetchTechManagers()
      .then((tms) => { if (mountedRef.current) setTechManagers(tms); })
      .catch(() => {});
  }, [isAdmin, techManager]);

  // Load CV list for non-admin on mount
  useEffect(() => {
    if (isAdmin) return;
    setCvLoading(true);
    listDocuments()
      .then((docs) => { if (mountedRef.current) { setCvList(docs.map((d) => ({ id: d.id, title: d.title, category_id: d.category_id }))); setCvLoading(false); } })
      .catch(() => { if (mountedRef.current) { setCvList([]); setCvLoading(false); } });
  }, [isAdmin]);

  // Load CV list for admin when employee is selected
  useEffect(() => {
    if (!isAdmin || !selectedEmployeeCode) {
      if (isAdmin) { setCvList(null); setCvLoading(false); setSelectedCvId(""); }
      return;
    }
    setCvList(null);
    setCvLoading(true);
    fetchEmployeeCvsForAdmin(selectedEmployeeCode)
      .then((list) => { if (mountedRef.current) { setCvList(list); setCvLoading(false); } })
      .catch(() => { if (mountedRef.current) { setCvList([]); setCvLoading(false); } });
  }, [isAdmin, selectedEmployeeCode]);

  // Notify parent whenever the selected CV (or its list) changes, so a sibling
  // cover-letter section can auto-match by category_id.
  useEffect(() => {
    if (!onCvSelected) return;
    if (selectedCvId === "") { onCvSelected("", null); return; }
    const cv = (cvList ?? []).find((c) => c.id === selectedCvId);
    onCvSelected(selectedCvId, cv?.category_id ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCvId, cvList]);

  async function runEnhancement() {
    const cvId = selectedCvId as number;
    if (!cvId) return;
    if (isAdmin && !selectedEmployeeCode) return;
    setPhase("enhancing");
    setError(null);
    try {
      const result = await enhanceCvForJob(job.id, cvId, isAdmin, isAdmin ? selectedEmployeeCode : undefined);
      if (mountedRef.current) {
        setEnhancedData(result.enhanced_cv_data as CvDocument);
        setPhase("done");
        setAlertId(result.alert_id);
        if (result.has_enhancement_logs) setHasLogs(true);
      }
    } catch (err: any) {
      if (mountedRef.current) { setError(err?.response?.data?.detail ?? "Enhancement failed. Please try again."); setPhase("error"); }
    }
  }

  async function reEnhance() {
    const cvId = (selectedCvId as number) || job.cv_id_enhanced;
    if (!cvId) return;
    if (isAdmin && !selectedEmployeeCode) {
      setError("Cannot re-enhance: no employee is linked to this job.");
      return;
    }
    setPhase("enhancing");
    setError(null);
    try {
      const result = await enhanceCvForJob(job.id, cvId, isAdmin, isAdmin ? selectedEmployeeCode : undefined);
      if (mountedRef.current) {
        setEnhancedData(result.enhanced_cv_data as CvDocument);
        setPhase("done");
        setAlertId(result.alert_id);
        if (result.has_enhancement_logs) setHasLogs(true);
      }
    } catch (err: any) {
      if (mountedRef.current) { setError(err?.response?.data?.detail ?? "Enhancement failed. Please try again."); setPhase("error"); }
    }
  }

  const canEnhance = selectedCvId !== "" && (!isAdmin || selectedEmployeeCode !== "");

  useImperativeHandle(ref, () => ({ runEnhancement, canEnhance, selectedCvId }));

  // ── Done ────────────────────────────────────────────────────────────────────
  if (phase === "done" && enhancedData) {
    return (
      <>
        <div className="flex items-center gap-2 flex-wrap">
          <FileText className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
          <button
            onClick={() => setShowModal(true)}
            className="text-xs text-emerald-400 hover:text-emerald-300 font-inter font-medium underline underline-offset-2 transition-colors"
          >
            View Enhanced CV
          </button>
          {hasLogs && alertId && (
            <button
              onClick={() => setShowLogsModal(true)}
              className="text-xs text-muted-foreground hover:text-primary font-inter underline underline-offset-2 transition-colors"
            >
              Enhancement logs
            </button>
          )}
          <button
            onClick={reEnhance}
            title="Re-enhance with the same CV"
            className="flex items-center gap-1 px-1.5 py-0.5 text-muted-foreground hover:text-primary rounded transition-colors"
          >
            <RotateCcw className="w-3 h-3 shrink-0" />
            <span className="text-[10px] font-inter underline underline-offset-2">Re-Enhance</span>
          </button>
          {techManager && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground font-inter ml-auto">
              <User className="w-3 h-3 shrink-0" />
              {techManager.name}
            </span>
          )}
        </div>
        {showModal && (
          <EnhancedCvModal
            enhancedData={enhancedData}
            jobTitle={job.job_title}
            jobDescription={jobSummary ?? job.job_summary}
            isAdmin={isAdmin}
            cvId={(selectedCvId as number) || job.cv_id_enhanced}
            employeeCode={isAdmin ? selectedEmployeeCode : undefined}
            onClose={() => setShowModal(false)}
          />
        )}
        {showLogsModal && alertId && (
          <EnhancementLogsModal alertId={alertId} isAdmin={isAdmin} onClose={() => setShowLogsModal(false)} />
        )}
      </>
    );
  }

  // ── Enhancing ───────────────────────────────────────────────────────────────
  if (phase === "enhancing") {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground font-inter">
        <Loader2 className="w-3.5 h-3.5 animate-spin text-primary shrink-0" />
        Enhancing CV… this may take a minute
        {techManager && (
          <span className="flex items-center gap-1 text-muted-foreground font-inter">
            <User className="w-3 h-3 shrink-0" />
            {techManager.name}
          </span>
        )}
      </div>
    );
  }

  // ── Idle / error — inline dropdowns ─────────────────────────────────────────
  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="flex items-center gap-1.5 text-xs text-primary font-inter font-medium shrink-0">
          <Sparkles className="w-3.5 h-3.5 shrink-0" />
          {showOwnTrigger ? "Enhance CV for this job" : "CV"}
        </span>

        {/* TM dropdown — admin without pre-linked TM only */}
        {isAdmin && !techManager && (
          <select
            value={selectedEmployeeCode}
            onChange={(e) => { setSelectedEmployeeCode(e.target.value); setSelectedCvId(""); }}
            className="bg-surface-muted border border-border rounded-lg px-2 py-1 text-xs text-foreground font-inter cursor-pointer hover:border-border focus:outline-none focus:border-primary transition-colors"
          >
            <option value="" className="bg-surface text-muted-foreground">— select TM —</option>
            {techManagers.map((tm) => (
              <option key={tm.employee_code} value={tm.employee_code} className="bg-surface text-foreground">
                {tm.name}
              </option>
            ))}
          </select>
        )}

        {/* CV dropdown */}
        <select
          value={String(selectedCvId)}
          onChange={(e) => setSelectedCvId(e.target.value ? Number(e.target.value) : "")}
          disabled={cvLoading || (isAdmin && !selectedEmployeeCode)}
          className="bg-surface-muted border border-border rounded-lg px-2 py-1 text-xs text-foreground font-inter cursor-pointer hover:border-border focus:outline-none focus:border-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <option value="">
            {cvLoading
              ? "Loading CVs…"
              : isAdmin && !selectedEmployeeCode
              ? "— select TM first —"
              : cvList?.length === 0
              ? "No CVs found"
              : "— select CV —"}
          </option>
          {(cvList ?? []).map((cv) => (
            <option key={cv.id} value={String(cv.id)}>
              {cv.title}
            </option>
          ))}
        </select>

        {showOwnTrigger && (
          <button
            onClick={runEnhancement}
            disabled={!canEnhance}
            className="px-2.5 py-1 bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed text-primary-foreground text-xs font-inter font-medium rounded-lg transition-colors shrink-0"
          >
            Enhance
          </button>
        )}

        {techManager && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground font-inter shrink-0 ml-auto">
            <User className="w-3 h-3 shrink-0" />
            {techManager.name}
          </span>
        )}
      </div>
      {error && <p className="text-[11px] text-destructive font-inter mt-1">{error}</p>}
    </>
  );
});

export default CvEnhancementSection;
